export class CacheService {
    static useLocalStorage = true;
    static defaultTTL = 60 * 1000; // 1 minute

    static get(key, ttl = CacheService.defaultTTL) {
        if (CacheService.useLocalStorage) {
            const cached = localStorage.getItem(key);
            if (cached) {
                const cacheData = JSON.parse(cached);
                const now = Date.now();
                
                if (cacheData.timestamp && (now - cacheData.timestamp) < ttl) {
                    return cacheData.data;
                } else {
                    localStorage.removeItem(key);
                    return null;
                }
            }
            return null;
        }
        return null;
    }

    static set(key, data) {
        if (CacheService.useLocalStorage) {
            const cacheData = {
                data: data,
                timestamp: Date.now()
            };

            try {
                localStorage.setItem(key, JSON.stringify(cacheData));
            } catch (e) {
                // If quota exceeded, try to sanitize client-only large fields and retry.
                // This prevents large File/dataUrl blobs from filling localStorage.
                try {
                    const sanitized = CacheService._sanitizeForStorage(data);
                    const retryCache = { data: sanitized, timestamp: Date.now() };
                    localStorage.setItem(key, JSON.stringify(retryCache));
                    console.warn('CacheService: Stored sanitized cache for', key);
                } catch (e2) {
                    // As a last resort, try removing photo arrays entirely if present
                    try {
                        const shallow = CacheService._removePhotos(data);
                        const retryCache2 = { data: shallow, timestamp: Date.now() };
                        localStorage.setItem(key, JSON.stringify(retryCache2));
                        console.warn('CacheService: Stored shallow cache for', key);
                    } catch (e3) {
                        // Give up - log and continue without caching to avoid blowing up the app
                        console.error('CacheService: Failed to store cache for', key, e3);
                    }
                }
            }
        }
    }

    // Remove client-only fields that are large or not serializable for storage.
    static _sanitizeForStorage(obj) {
        const seen = new WeakSet();

        function recurse(value) {
            if (value === null || typeof value !== 'object') return value;
            if (seen.has(value)) return undefined;
            seen.add(value);

            if (Array.isArray(value)) {
                return value.map(v => recurse(v)).filter(v => typeof v !== 'undefined');
            }

            const out = {};
            for (const [k, v] of Object.entries(value)) {
                // Strip fields known to be large or client-only
                // Keep server-provided paths and SAS-tokened preview URLs (path, thumbPath, pathIncSASToken, thumbPathIncSASToken)
                if (k === 'file' || k === 'dataUrl') {
                    continue;
                }
                // Strip inline data URIs
                if (typeof v === 'string' && v.startsWith('data:')) continue;

                // Otherwise recurse and preserve known path/SAS fields
                out[k] = recurse(v);
            }
            return out;
        }

        return recurse(obj);
    }

    // Aggressive removal: replace photos arrays with a count-only placeholder
    static _removePhotos(obj) {
        try {
            const clone = JSON.parse(JSON.stringify(obj));
            function walk(o) {
                if (!o || typeof o !== 'object') return;
                for (const k of Object.keys(o)) {
                    if (k === 'photos' && Array.isArray(o[k])) {
                        o[k] = { __removed_for_storage: true, count: o[k].length };
                    } else {
                        walk(o[k]);
                    }
                }
            }
            walk(clone);
            return clone;
        } catch (e) {
            return {};
        }
    }

    static clear(key) {
        if (CacheService.useLocalStorage) {
            localStorage.removeItem(key);
        }
    }
}